<template>
  <div>
    <v-card>
      <v-card-title
        class="card-title d-flex justify-space-between align-center"
        :style="{ ['background-color']: cardColor, color: 'white' }"
      >
        {{ cardTitle }}
        <div class="d-flex">
          <v-tooltip top nudge-top="18">
            <template #activator="{ attrs, on }">
              <v-icon class="mx-2" v-bind="attrs" v-on="on" size="20" dark> mdi-information-outline </v-icon>
            </template>
            <span>{{ cardInfoText }}</span>
          </v-tooltip>
          <v-tooltip top nudge-top="18">
            <template #activator="{ attrs, on }">
              <v-icon @click="expandCard" v-bind="attrs" v-on="on" size="20" dark> mdi-arrow-expand </v-icon>
            </template>
            <span>Expandir / Ver mais</span>
          </v-tooltip>
        </div>
      </v-card-title>
      <v-card-text v-if="isLoading" class="d-flex justify-center align-center" style="min-height: 650px">
        <v-progress-circular width="5" :size="80" indeterminate :color="cardColor" />
      </v-card-text>
      <v-card-text v-else :style="`position: relative; height: ${isTranshipment ? '590' : '730'}px`">
        <div v-if="isPaginationLoading">
          <v-progress-linear indeterminate :color="cardColor" absolute style="top: 0px" />
        </div>
        <div v-if="totalRecords === 0" class="pt-5 pb-1 d-flex justify-center">
          <span style="color: #a2a2a2">Nenhuma entrega encontrada</span>
        </div>
        <div v-else style="height: 100%">
          <div class="d-flex flex-column justify-space-between" style="height: 100%">
            <div>
              <div v-for="(delivery, i) in deliveries" :key="i">
                <delivery-card :delivery="delivery" :is-transhipment="isTranshipment" />
              </div>
            </div>

            <div class="d-flex flex-column align-center mt-4">
              <v-pagination
                v-model="pageNumber"
                :length="totalPages"
                :total-visible="5"
                @input="onPageChange"
              ></v-pagination>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import DeliveryCard from '@/components/Management/DeliveryCard.vue';
import { mapActions } from 'vuex';
import { ExpandedManagementCardHandler } from '@/store/modules/expandedManagementCard';
import { ExpandedTranshipmentManagementCardHandler } from '@/store/modules/expandedTranshipmentManagementCard';
export default {
  components: {
    DeliveryCard
  },

  props: {
    type: {
      default: '',
      type: String
    },
    isTranshipment: {
      default: false,
      type: Boolean
    }
  },

  data: () => ({
    deliveries: [],
    isLoading: false,
    isPaginationLoading: false,
    totalPages: 0,
    pageNumber: 1,
    totalRecords: 0
  }),

  computed: {
    cardObjectByType() {
      const types = {
        onTime: this.getCardObjectStructure(
          'Em dia',
          '#0988BC',
          'getShipmentsManagementCtesOnTime',
          'Entregas que estão no 3° dia'
        ),
        critical: this.getCardObjectStructure(
          'Dia crítico',
          '#F46F22',
          'getShipmentsManagementCtesCriticalDay',
          'Entregas que estão no 4° dia'
        ),
        late: this.getCardObjectStructure(
          'Atrasadas',
          '#E92028',
          'getShipmentsManagementCtesLate',
          'Entregas que estão no 5° dia para frente'
        ),
        transhipmentOnTime: this.getCardObjectStructure(
          'Em dia',
          '#0988BC',
          'getTranshipmentsManagementCtesOnTime',
          'Entregas que estão no 3° dia'
        ),
        transhipmentCritical: this.getCardObjectStructure(
          'Dia crítico',
          '#F46F22',
          'getTranshipmentsManagementCtesCriticalDay',
          'Entregas que estão no 4° dia'
        ),
        transhipmentLate: this.getCardObjectStructure(
          'Atrasadas',
          '#E92028',
          'getTranshipmentsManagementCtesLate',
          'Entregas que estão no 5° dia para frente'
        )
      };

      const cardObjectByType = types[this.type] || types.onTime;

      return cardObjectByType;
    },

    cardTitle() {
      return this.cardObjectByType.title;
    },

    cardColor() {
      return this.cardObjectByType.color;
    },

    cardInfoText() {
      return this.cardObjectByType.infoText;
    },

    shouldRefresh() {
      return ExpandedManagementCardHandler.getShouldRefresh(this);
    }
  },

  watch: {
    shouldRefresh: {
      handler(shouldRefresh) {
        if (shouldRefresh) {
          this.refreshWhenTimerEnds();
        }
      }
    }
  },

  async created() {
    this.isLoading = true;
    await this.fetchDeliveriesByType();
    this.isLoading = false;
  },

  methods: {
    ...mapActions('ShipmentStore', [
      'getShipmentsManagementCtesOnTime',
      'getShipmentsManagementCtesCriticalDay',
      'getShipmentsManagementCtesLate',
      'getTranshipmentsManagementCtesOnTime',
      'getTranshipmentsManagementCtesCriticalDay',
      'getTranshipmentsManagementCtesLate'
    ]),

    async fetchDeliveriesByType() {
      try {
        const { functionName: fetchDeliveries } = this.cardObjectByType;

        const { data, totalPages, totalRecords } = await this[fetchDeliveries]({
          pageNumber: this.pageNumber,
          pageSize: 3
        });
        this.deliveries = data;
        this.totalPages = totalPages;
        this.totalRecords = totalRecords;
      } catch (error) {
        console.error(error);
      }
    },

    async onPageChange() {
      this.isPaginationLoading = true;
      await this.fetchDeliveriesByType(false);
      this.isPaginationLoading = false;
    },

    getCardObjectStructure(title, color, functionName, infoText) {
      return { title, color, functionName, infoText };
    },

    expandCard() {
      if (this.isTranshipment) {
        ExpandedTranshipmentManagementCardHandler.setIsCardExpanded(this, true);
        ExpandedTranshipmentManagementCardHandler.setExpandedManagementCardObject(this, this.cardObjectByType);
      } else {
        ExpandedManagementCardHandler.setIsCardExpanded(this, true);
        ExpandedManagementCardHandler.setExpandedManagementCardObject(this, this.cardObjectByType);
      }
    },

    async refreshWhenTimerEnds() {
      this.isPaginationLoading = true;
      await this.fetchDeliveriesByType(false);
      this.isPaginationLoading = false;

      ExpandedManagementCardHandler.setShouldRefreshWhenTimerEnds(this, false);
      ExpandedTranshipmentManagementCardHandler.setShouldRefreshWhenTimerEnds(this, false);
    }
  }
};
</script>

<style></style>
